<template>
  <SwitchGroup as="div" class="ui-switch" :class="mainClasses" @click.stop="emit('click', $event)">
    <SwitchLabel
      v-if="label"
      as="span"
      class="ui-switch__label-wrapper"
    >
      <span class="ui-switch__label">{{ label }}</span>
      <span v-if="description" class="ui-switch__description">
        {{ description }}
      </span>
    </SwitchLabel>
    <Switch
      :model-value="modelValue"
      :disabled
      v-bind="$attrs"
      class="ui-switch__toggle"
      :class="toggleClasses"
      :data-refid
      @update:model-value="handleUpdate"
    >
      <span class="sr-only">{{ label || 'Use setting' }}</span>
      <span
        aria-hidden="true"
        class="ui-switch__circle"
        :class="circleClasses"
      />
    </Switch>
  </SwitchGroup>
  <p v-if="error" class="ui-switch__error">
    {{ error }}
  </p>
</template>

<script setup lang="ts">
import { Switch, SwitchGroup, SwitchLabel } from '@headlessui/vue'
import { computed } from 'vue'

type Props = {
  label?: string
  description?: string

  dataRefid?: string

  inverse?: boolean
  passive?: boolean
  full?: boolean
  leading?: boolean

  error?: string
  disabled?: boolean
  readonly?: boolean
}

type Emits = {
  click: [event: MouseEvent]
}

const emit = defineEmits<Emits>()

const props = defineProps<Props>()

const modelValue = defineModel<boolean>()

const mainClasses = computed(() => ({
  'ui-switch--full': props.full,
  'ui-switch--disabled': props.disabled,
  'ui-switch--inverse': props.inverse,
  'ui-switch--leading': props.leading,
}))

const toggleClasses = computed(() => ({
  'ui-switch--passive': props.passive,
  'ui-switch__toggle--active': modelValue.value && !props.passive,
}))

const circleClasses = computed(() => ({
  'ui-switch__circle--active': modelValue.value,
}))

const handleUpdate = (value: boolean) => {
  if (props.readonly) return
  modelValue.value = value
}
</script>

<style>
.ui-switch {
  @apply flex items-center gap-2;

  &--leading {
    @apply flex-row-reverse justify-end;
  }

  &--full {
    @apply justify-between;
  }

  &--disabled {
    @apply opacity-50;
    @apply cursor-default;
  }

  &__toggle {
    @apply h-4 w-9;
    @apply relative inline-flex shrink-0;
    @apply border-2 border-transparent;
    @apply rounded-full;
    @apply bg-gray-200 dark:bg-gray-600;
    @apply focus:outline-none focus:ring-2 focus:ring-offset-2 ring-offset-body;
    @apply focus:ring-indigo-500 dark:focus:ring-indigo-400;
    @apply cursor-pointer;

    &--active {
      @apply bg-indigo-600 dark:bg-indigo-400;
    }
  }

  &__circle {
    @apply size-3;
    @apply inline-block;
    @apply bg-white;
    @apply shadow rounded-full;
    @apply pointer-events-none;

    &--active {
      @apply translate-x-5;
    }

    .ui-switch--inverse & {
      @apply translate-x-5;

      &--active {
        @apply translate-x-0;
      }
    }
  }

  &__label-wrapper {
    @apply flex flex-col;
    @apply leading-none;
  }

  &__label {
    @apply text-sm sm:text-xs whitespace-nowrap;
    @apply text-gray-500 dark:text-gray-400;
  }

  &__description {
    @apply text-xs sm:text-[0.625rem];
    @apply text-gray-500 dark:text-gray-400;
  }

  &__error {
    @apply mt-1;
    @apply text-sm sm:text-xs;
    @apply text-red-600 dark:text-red-400;
  }
}
</style>
